import { Avatar, Box } from '@mui/material';
import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CustomTypography } from 'src/components/custom/CustomTypography';
import { ConfirmationModal } from 'src/components/modal/ConfirmationModal';
import { EmptyProviders } from 'src/components/Providers/editProvidersPanel/EmptyList';
import { IconWithTooltip } from 'src/components/shared/IconWithTooltip';
import { SidePanel } from 'src/components/sidePanel/SidePanel';
import { getImageURL, getProviderTitleByType } from 'src/helpers';
import { setNotice } from 'src/redux/noticesSlice';
import {
  EGetProviderAction,
  IProvider,
  useDeleteProviderMutation,
  useGetProvidersQuery,
} from 'src/redux/services/provider';
import { ReactComponent as IdIcon } from '../../../icons/Id.svg';
import { addProvidersTypes, addProviderType } from '../Settings';
import { ChooseProvider } from './ChooseProvider';
import { Edit1CProvider } from './edit/Edit1CProvider';
import { EditIdmProvider } from './edit/EditIdmProvider';
import { EditLdapProvider } from './edit/EditLdapProvider';
import styles from './EditProviders.module.css';
import { ENoticeType } from 'src/enums';

interface IEditTrustedProviders {
  isOpen: boolean;
  onClose: () => void;
}

export const EditTrustedProviders: FC<IEditTrustedProviders> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();

  const ldapProviderAvatar = 'public/default/ldap.svg';
  const _1CProviderAvatar = 'public/default/1c.svg';
  const idmProviderAvatar = 'public/default/idm.svg';

  const { appId = '', clientId = '' } = useParams<{ appId: string; clientId: string }>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [providerToEdit, setProviderToEdit] = useState<IProvider | null>(null);
  const [providerToDelete, setProviderToDelete] = useState<IProvider | null>(null);
  const [providers, setProviders] = useState<IProvider[]>([]);
  const [fetchingProviderIds, setFetchingProviderIds] = useState<string[]>([]);
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isEditLdapFormOpen, setIsEditLdapFormOpen] = useState(false);
  const [isEdit1CFormOpen, setIsEdit1CFormOpen] = useState(false);
  const [isEditIdmFormOpen, setIsEditIdmFormOpen] = useState(false);
  const { data: appProviders, isFetching: providersFetching } = useGetProvidersQuery({
    client_id: clientId || appId,
    query: {
      action: EGetProviderAction.all,
      types: [
        addProviderType.LDAP,
        addProviderType.ALDPRO,
        addProviderType._1C,
        addProviderType.IDM,
      ].join(','),
    },
  });

  const [deleteProvider] = useDeleteProviderMutation();

  useEffect(() => {
    if (appProviders) {
      setProviders(appProviders);
    }
  }, [appProviders]);

  useEffect(() => {
    if (!isEditFormOpen) setProviderToEdit(null);
  }, [isEditFormOpen]);

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setProviderToDelete(null);
  };

  const handleDeleteProvider = async () => {
    if (providerToDelete) {
      setFetchingProviderIds((ids) => [...ids, providerToDelete.id]);

      await deleteProvider({
        clientId: clientId || appId,
        providerId: providerToDelete.id,
      });
      setProviderToDelete(null);
      setDeleteModalOpen(false);

      setFetchingProviderIds(fetchingProviderIds.filter((id) => id !== providerToDelete.id));
    }
  };

  const createProviderSettings = async (provider: IProvider) => {
    setProviderToEdit(provider);

    switch (provider.type) {
      case addProviderType.LDAP:
      case addProviderType.ALDPRO:
        return setIsEditLdapFormOpen(true);
      case addProviderType._1C:
        return setIsEdit1CFormOpen(true);
      case addProviderType.IDM:
        return setIsEditIdmFormOpen(true);
      default:
        setIsEditFormOpen(true);
    }
  };

  const handleCopyProvider = async (provider: IProvider) => {
    dispatch(
      setNotice({
        type: ENoticeType.info,
        id: Math.random(),
        isRead: false,
        message: `Настройки провайдера сохранены в буфер`,
        timestamp: new Date().toString(),
      }),
    );

    await navigator.clipboard.writeText(JSON.stringify(provider));
  };

  return (
    <>
      <SidePanel
        onClose={onClose}
        isOpen={isOpen}
        title={'Настроить провайдеры'}
        AdditionalAction={() => setIsCreateFormOpen(true)}
      >
        <div className={styles.wrapper}>
          {!providers.length && (
            <EmptyProviders title="Провайдеров" onClick={() => setIsCreateFormOpen(true)} />
          )}
          {providers.slice().map((provider) => {
            return (
              <div
                className={clsx(styles.provider, {
                  [styles['checked-provider']]: provider.is_active,
                })}
                key={provider.id}
              >
                <Avatar
                  src={
                    provider.avatar instanceof File
                      ? URL.createObjectURL(provider.avatar)
                      : provider.avatar?.startsWith('data:')
                      ? provider.avatar
                      : getImageURL(provider.avatar)
                  }
                  className={styles['provider-icon-wrapper']}
                >
                  {!provider.avatar && <IdIcon />}
                </Avatar>
                <div className={styles['provider-name-wrapper']}>
                  <CustomTypography className={clsx('text-14', styles['provider-name'])}>
                    {provider.name}
                  </CustomTypography>
                  <CustomTypography className={clsx('text-12')} color="grey">
                    {getProviderTitleByType(provider.type, addProvidersTypes)}
                  </CustomTypography>
                </div>
                {(provider.client_id === clientId || provider.client_id === appId) && (
                  <div className={styles['provider-buttons']}>
                    <IconWithTooltip
                      dataAttribute={'delete-provider-button'}
                      iconType="delete"
                      onClick={() => {
                        setProviderToDelete(provider);
                        setDeleteModalOpen(true);
                      }}
                      disabled={
                        !!fetchingProviderIds.find((id) => id === provider.id) || providersFetching
                      }
                    />
                    <IconWithTooltip
                      dataAttribute={'edit-provider-button'}
                      iconType="edit"
                      onClick={() => createProviderSettings(provider)}
                    />
                    <IconWithTooltip
                      dataAttribute={'copy-provider-button'}
                      iconType="copy"
                      placement="bottom-end"
                      onClick={() => handleCopyProvider(provider)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </SidePanel>
      <ChooseProvider isOpen={isCreateFormOpen} onClose={() => setIsCreateFormOpen(false)} />
      <EditLdapProvider
        defaultAvatar={ldapProviderAvatar}
        provider={providerToEdit}
        isOpen={isEditLdapFormOpen}
        onClose={() => {
          setIsEditLdapFormOpen(false);
        }}
      />
      <Edit1CProvider
        defaultAvatar={_1CProviderAvatar}
        provider={providerToEdit}
        isOpen={isEdit1CFormOpen}
        onClose={() => {
          setIsEdit1CFormOpen(false);
        }}
      />
      <EditIdmProvider
        defaultAvatar={idmProviderAvatar}
        provider={providerToEdit}
        isOpen={isEditIdmFormOpen}
        onClose={() => {
          setIsEditIdmFormOpen(false);
        }}
      />

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onAction={handleDeleteProvider}
        disabled={!!fetchingProviderIds.find((id) => id === providerToDelete?.id)}
        title="Удалить способ входа"
        mainMessage={[
          'Способ входа будет удален. Если способ входа используется в приложении, пользователи не смогут войти в него, используя данный способ входа.',
        ]}
      />
    </>
  );
};
